import { TFunction } from 'i18next'
import {
  SOCIAL_POST_SOURCE_LABEL_AVAILABLE,
  SOCIAL_POST_SOURCES,
  SocialPlatform,
  SocialPostSource,
  SocialSyncSchedule,
  SocialTrackerCadence,
  SocialPostItem,
  SocialSentiment,
  SocialTrackerItem,
  SocialWindow,
} from '@digital-twins/shared-types'
import { intlLocaleForUi } from '@/i18n'
import { ROUTES } from '@/routes/routes'

/** One cadence for card labels and list filtering when collection is manual-only. */
export function effectiveSocialCadence(cadence: SocialTrackerCadence, schedule: SocialSyncSchedule): SocialTrackerCadence {
  return schedule === 'manual' ? 'on_demand' : cadence
}

/** Platform names are product names: not translated. */
export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  reddit: 'Reddit',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
}

/**
 * The source label a post shows: the judge's label when it meets its bar on
 * the post's platform (SOCIAL_POST_SOURCE_LABEL_AVAILABLE); null when the
 * source is unknown or the label is not available there. The pill and the
 * Source facet both read it.
 */
export function socialShownPostSource(
  platform: SocialPlatform,
  source: SocialPostSource | null | undefined,
): SocialPostSource | null {
  return source && SOCIAL_POST_SOURCE_LABEL_AVAILABLE[platform][source] ? source : null
}

/**
 * The Source facet over `platforms` (the selected channel, else the Topic's
 * channels): the labels available on at least one of them, and per other
 * label the platforms where it is not available (stated under the menu).
 */
export function socialSourceFacet(platforms: ReadonlyArray<SocialPlatform>): {
  sources: Array<SocialPostSource>
  unavailable: Array<{ source: SocialPostSource; platforms: Array<SocialPlatform> }>
} {
  const sources = SOCIAL_POST_SOURCES.filter((source) =>
    platforms.some((platform) => SOCIAL_POST_SOURCE_LABEL_AVAILABLE[platform][source]),
  )
  const unavailable = SOCIAL_POST_SOURCES.map((source) => ({
    source,
    platforms: platforms.filter((platform) => !SOCIAL_POST_SOURCE_LABEL_AVAILABLE[platform][source]),
  })).filter((entry) => entry.platforms.length > 0)
  return { sources, unavailable }
}

/** Embed frame size per platform, shared by the post detail and the citation source. */
/**
 * The label key of an engagement metric on a platform: Reddit's `likes`
 * counter is the post score (upvotes minus downvotes), so it reads "score".
 */
export function socialMetricLabelKey<K extends 'likes' | 'comments' | 'shares' | 'views'>(
  platform: SocialPlatform,
  key: K,
): K | 'score' {
  if (platform === 'reddit' && key === 'likes') {
    return 'score'
  }
  return key
}

export const EMBED_SIZE_CLASS: Record<SocialPlatform, string> = {
  reddit: 'h-[480px]',
  youtube: 'aspect-video',
  tiktok: 'h-[740px] max-w-[340px]',
  instagram: 'h-[720px] max-w-[540px]',
}

export function formatSocialDateTime(
  iso: string | null,
  uiLanguage: string,
): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function formatSocialDate(
  iso: string | null,
  uiLanguage: string,
): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    dateStyle: 'medium',
  }).format(date)
}

export function formatSocialCount(value: number, uiLanguage: string): string {
  return new Intl.NumberFormat(intlLocaleForUi(uiLanguage), {
    notation: 'compact',
  }).format(value)
}

/** Deep-link windows, in days; the dates are fixed when the thread starts. */
const SOCIAL_ASK_WINDOW_DAYS = { '7d': 7 } as const
export type SocialAskWindow = keyof typeof SOCIAL_ASK_WINDOW_DAYS

const DAY_MS = 24 * 60 * 60 * 1000
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Where a Topic Ask was pressed (plan 2026-11-01 D1): the Topic, and the
 * tracker, post, theme, brand and filters on screen. Public tokens and config
 * ids only. The thread keeps it as a focus note: it steers, never restricts.
 */
export interface SocialAskFocus {
  topicToken: string
  trackerToken?: string | null
  postToken?: string | null
  themeId?: string | null
  brandId?: string | null
  platform?: SocialPlatform | null
  sentiment?: SocialSentiment | null
  source?: SocialPostSource | null
  /** A preset ending now, or the whole UTC days the Topic's date filter spans. */
  window?: SocialAskWindow | SocialWindow | null
}

/**
 * The Topic focus a tracker's Ask carries: its Topic with the tracker pinned,
 * or null while it has no posts to ask about (or, on servers before Topics,
 * no Topic).
 */
export function trackerAskFocus(
  tracker: Pick<SocialTrackerItem, 'token' | 'universeToken' | 'totalPosts'>,
  window?: SocialAskWindow,
): SocialAskFocus | null {
  if (!tracker.universeToken || tracker.totalPosts === 0) {
    return null
  }
  return {
    topicToken: tracker.universeToken,
    trackerToken: tracker.token,
    ...(window ? { window } : {}),
  }
}

/** The `window` param for a day window: `YYYY-MM-DD..YYYY-MM-DD`. */
function socialAskWindowParam(window: SocialAskWindow | SocialWindow): string | null {
  if (typeof window === 'string') {
    return window
  }
  const from = window.from.slice(0, 10)
  const to = window.to.slice(0, 10)
  return ISO_DAY.test(from) && ISO_DAY.test(to) ? `${from}..${to}` : null
}

/**
 * Home (no question) or a new thread (with `question`) carrying the Topic
 * focus as `topic`, `tracker`, `post`, `theme`, `brand`, `platform`,
 * `sentiment`, `source` and `window`. The thread stays in Auto with the library and
 * the web on (D0).
 */
export function askAboutTopicHref(
  focus: SocialAskFocus,
  options: { question?: string } = {},
): string {
  const params = new URLSearchParams()
  params.set('topic', focus.topicToken)
  const optional: Array<[string, string | null | undefined]> = [
    ['tracker', focus.trackerToken],
    ['post', focus.postToken],
    ['theme', focus.themeId],
    ['brand', focus.brandId],
    ['platform', focus.platform],
    ['sentiment', focus.sentiment],
    ['source', focus.source],
    ['window', focus.window ? socialAskWindowParam(focus.window) : null],
  ]
  for (const [key, value] of optional) {
    if (value) {
      params.set(key, value)
    }
  }
  if (!options.question) {
    return `${ROUTES.home}?${params.toString()}`
  }
  params.set('q', options.question)
  return `${ROUTES.threads}?${params.toString()}`
}

/**
 * The question the Topic's main Ask starts a thread with, built from the
 * Topic's own name. A view narrowed by anything the focus carries says so,
 * so the generic question never reads as the whole Topic while the focus
 * pins less. Filters the focus cannot carry (search, one author, the Local
 * switch) do not count.
 */
export function socialAskTopicQuestion(
  t: TFunction<'app'>,
  focus: SocialAskFocus,
  topicName: string,
): string {
  const isNarrowed = Boolean(
    focus.themeId ||
      focus.brandId ||
      focus.platform ||
      focus.sentiment ||
      focus.source ||
      focus.window,
  )
  return isNarrowed
    ? t('socialListening.topicAsk.mainInsightsFiltered', { name: topicName })
    : t('socialListening.topicAsk.mainInsights', { name: topicName })
}

/** Characters of a post's text an Ask question quotes. */
const ASK_POST_EXCERPT_CHARS = 80

/**
 * The question "Ask about this post" starts a Topic thread with. The link
 * carries the post's token as the focus; the question names the post the way
 * a reader would: its platform, its author and the start of its text (one
 * author often has several posts in a Topic). Without text it names the
 * author, without an author the text, with neither the Topic. Never a row id
 * or token.
 */
export function socialAskPostQuestion(
  t: TFunction<'app'>,
  post: Pick<SocialPostItem, 'platform' | 'authorHandle' | 'text'>,
  topicName: string,
): string {
  const platform = SOCIAL_PLATFORM_LABELS[post.platform]
  const text = post.text.replace(/\s+/g, ' ').trim()
  const excerpt =
    text.length > ASK_POST_EXCERPT_CHARS
      ? `${text.slice(0, ASK_POST_EXCERPT_CHARS).trimEnd()}…`
      : text
  if (post.authorHandle) {
    const author = `@${post.authorHandle}`
    return excerpt
      ? t('socialListening.topicAsk.postByExcerpt', { platform, author, excerpt })
      : t('socialListening.topicAsk.post', { platform, author })
  }
  if (excerpt) {
    return t('socialListening.topicAsk.postExcerpt', { platform, excerpt })
  }
  return t('socialListening.topicAsk.postPlatform', { platform, name: topicName })
}

/** `YYYY-MM-DD..YYYY-MM-DD` as whole UTC days, either order; null when unreadable. */
function socialDayRangeFromParam(param: string): SocialWindow | null {
  const [first, last, ...rest] = param.split('..')
  if (rest.length > 0 || !first || !last || !ISO_DAY.test(first) || !ISO_DAY.test(last)) {
    return null
  }
  if (Number.isNaN(Date.parse(first)) || Number.isNaN(Date.parse(last))) {
    return null
  }
  const [from, to] = first <= last ? [first, last] : [last, first]
  return { from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` }
}

/**
 * The `window` deep-link param as dates: a preset (`7d`) ends now, a day
 * range (`YYYY-MM-DD..YYYY-MM-DD`, a Topic's date filter) spans whole UTC
 * days. Null when absent or unknown.
 */
export function socialWindowFromParam(
  param: string | null,
  now: Date = new Date(),
): SocialWindow | null {
  if (param?.includes('..')) {
    return socialDayRangeFromParam(param)
  }
  const days =
    param && param in SOCIAL_ASK_WINDOW_DAYS
      ? SOCIAL_ASK_WINDOW_DAYS[param as SocialAskWindow]
      : null
  if (days == null) {
    return null
  }
  return {
    from: new Date(now.getTime() - days * DAY_MS).toISOString(),
    to: now.toISOString(),
  }
}

/** Words a display heading keeps at most before it ends on an ellipsis. */
const SOCIAL_HEADING_MAX_CHARS = 64

/**
 * A theme name fit for a heading: hashtags, mentions and emoji removed,
 * whitespace collapsed, cut on a word near 64 characters. Falls back to the
 * raw name when nothing readable is left.
 */
export function socialHeadingText(raw: string): string {
  const cleaned = raw
    .replace(new RegExp('[#@][\\p{L}\\p{N}_.]+', 'gu'), ' ')
    .replace(new RegExp('[\\p{Extended_Pictographic}\\u{FE0F}\\u{200D}]', 'gu'), ' ')
    .replace(/\s+/g, ' ')
    .replace(/[\s,;:.|-]+$/, '')
    .trim()
  if (!cleaned) {
    return raw.trim()
  }
  if (cleaned.length <= SOCIAL_HEADING_MAX_CHARS) {
    return cleaned
  }
  const cut = cleaned.slice(0, SOCIAL_HEADING_MAX_CHARS)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > 32 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.]+$/, '')}…`
}

/** Language codes ("en", "en-US") as names in the UI language ("English", "English (United States)"); the code when unknown. */
export function formatSocialLanguages(codes: Array<string>, uiLanguage: string): string {
  let names: Intl.DisplayNames | null = null
  try {
    names = new Intl.DisplayNames([intlLocaleForUi(uiLanguage)], { type: 'language' })
  } catch {
    names = null
  }
  const labels = codes.map((code) => {
    try {
      return names?.of(code) ?? code
    } catch {
      return code
    }
  })
  return Array.from(new Set(labels)).join(', ')
}
